import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppNavigate } from "../lib/route-primitives";
import {
  resolveJoinPalette, resolveJoinFontPair,
  formatWeddingDate, formatWeddingTime, formatWeddingWeekday, weddingEventName,
  listWeddingEvents, eventSourceFromRow, daysUntilEvent, headlineEvent,
} from "@wedding/shared";
import type { JoinThemeConfig } from "@wedding/shared";
import { langOf } from "../lib/wedding-event";
import type { ProgramEntry } from "./join/layouts/types";
import { useAuth } from "../contexts/auth-context";
import { useWedding } from "../contexts/wedding-context";
import { paths } from "../lib/paths";
import { useScrollSetup } from "./join/useScrollSetup";
import { PremiumLayoutHost } from "./join/PremiumLayoutHost";
import { HeroSection } from "./join/HeroSection";
import { CountdownSection } from "./join/CountdownSection";
import { StorySection } from "./join/StorySection";
import { VenueSection } from "./join/VenueSection";
import { GallerySection } from "./join/GallerySection";
import { JoinFormSection } from "./join/JoinFormSection";
import { JoinRegisterForm } from "./join/JoinRegisterForm";
import { WeddingProgram } from "./join/layouts/WeddingProgram";
import { getLayoutById } from "./join/layouts";

/** Deep-merge guard for objects only (arrays replaced, primitives replaced). */
function deepMerge<T>(base: T, override: unknown): T {
  if (override === null || override === undefined) return base;
  if (typeof base !== "object" || base === null || Array.isArray(base)) return override as T;
  if (typeof override !== "object" || Array.isArray(override)) return override as T;
  const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(override as Record<string, unknown>)) {
    result[k] = deepMerge(result[k], v);
  }
  return result as T;
}

export function JoinPage() {
  const { isAuthenticated } = useAuth();
  const { slug, wedding } = useWedding();
  const { i18n } = useTranslation();

  // Program TEK yerde biçimlendirilir; 20 tema hazır string'leri alır.
  const locale = langOf(i18n.language) === "tr" ? "tr-TR" : i18n.language;
  const program: ProgramEntry[] = useMemo(() => {
    const lang = langOf(i18n.language);
    return listWeddingEvents(eventSourceFromRow(wedding)).map((e) => ({
      key: e.which,
      name: weddingEventName(e, lang),
      dateTime: e.dateTime,
      dateLabel: formatWeddingDate(e.dateTime, locale),
      numericDateLabel: formatWeddingDate(e.dateTime, locale, {
        day: "2-digit", month: "2-digit", year: "numeric",
      }).replace(/[/\s]/g, "."),
      dayMonthLabel: formatWeddingDate(e.dateTime, locale, { day: "numeric", month: "long" }),
      weekdayLabel: formatWeddingWeekday(e.dateTime, locale),
      timeLabel: formatWeddingTime(e.dateTime, locale),
      location: e.location,
      locationShort: e.location?.split(",")[0]?.trim() ?? null,
      cityLabel: e.location?.split(",").pop()?.trim() || null,
      daysUntil: daysUntilEvent(e.dateTime),
    }));
  }, [wedding, i18n.language, locale]);

  // Tek "başlık" tarih/mekan gösteren yüzeyler (hero, mekan bölümü) geri
  // sayımın hedeflediği AKTİF etkinliği gösterir: iki tarihli düğünde ilk
  // tarih geçince başlık kendiliğinden ikinci etkinliğe döner — organizatör
  // hiçbir alan değiştirmez ve bu TÜM temalar için geçerli (prop tek yerden
  // gelir). `program` yine iki etkinliği de taşır (bkz. WeddingProgram).
  const { dateTime: focusDateTime, location: focusLocation } = useMemo(
    () => headlineEvent(eventSourceFromRow(wedding)),
    [wedding],
  );

  const joinConfig = (wedding?.config?.join ?? {}) as JoinThemeConfig;
  const layoutId = joinConfig.layoutId;
  const layoutDef = layoutId && layoutId !== "default" ? getLayoutById(layoutId) : undefined;

  // Legacy theme resolution — only used by default layout.
  const palette = resolveJoinPalette(joinConfig.palette);
  const fontPair = resolveJoinFontPair(joinConfig.fontPair);
  const heroUrl = wedding?.cover_photo_url ?? null;

  // Premium config must be a STABLE reference across re-renders. The wedding
  // context fires two renders on load (setWedding in .then, setIsLoading in
  // .finally); without memoization `config` would be a fresh object each time,
  // re-triggering the layout's `[cfg]` effect (Lenis/GSAP teardown+rebuild →
  // the hero intro visibly replays). Keyed on the actual config source only.
  const premiumConfig = useMemo(() => {
    if (!layoutDef || !wedding) return undefined;
    const merged = layoutDef.defaults !== undefined
      ? deepMerge(layoutDef.defaults, joinConfig.config ?? {})
      : undefined;
    const parsed = layoutDef.schema ? layoutDef.schema.safeParse(merged) : { success: true, data: merged };
    return parsed.success ? parsed.data : layoutDef.defaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutDef, wedding, joinConfig.config]);

  // Inject Google Fonts (legacy + premium layouts use Playfair/Inter via static <link>; this just keeps default working)
  useEffect(() => {
    if (layoutDef) return; // premium layouts ship their own fonts via @import or <style>
    const id = "join-theme-font";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${fontPair.googleFonts}&display=swap`;
  }, [fontPair.googleFonts, layoutDef]);

  // Premium layouts own their own Lenis; only the default layout needs this one.
  useScrollSetup(!layoutDef);

  if (isAuthenticated) {
    return <AppNavigate to={paths.wedding.home(slug)} replace />;
  }

  if (!wedding) return null;

  // ── Premium layout path ───────────────────────────────────────────────
  if (layoutDef) {
    // Tema arka planı yalnız ülke-seçim popover'ının zemini için gerekli;
    // formun geri kalanı currentColor'dan türer.
    const themeSurface = (premiumConfig as { colors?: { background?: string } } | undefined)
      ?.colors?.background;
    const layoutProps = {
      title: wedding.title,
      dateTime: focusDateTime,
      location: focusLocation,
      description: wedding.description,
      slug,
      coverPhotoUrl: heroUrl,
      galleryImages: wedding.join_cover_images ?? [],
      ambientVideoUrl: undefined,
      program,
      // GERÇEK kayıt formu — tema yalnız yerleştirir, kendi formunu yazmaz.
      formSlot: <JoinRegisterForm slug={slug} variant="theme" surface={themeSurface} />,
    };
    // Premium themes (dark) get the preload loader via PremiumLayoutHost; the
    // host mounts the layout only once media is ready so its intro plays once.
    if (layoutDef.tier === "premium") {
      return <PremiumLayoutHost layoutDef={layoutDef} {...layoutProps} config={premiumConfig} />;
    }
    // Original-tier layouts render directly (no dark loader over a light theme).
    const Layout = layoutDef.component;
    return <Layout {...layoutProps} config={premiumConfig} />;
  }

  // ── Default (legacy) layout ───────────────────────────────────────────
  const themeVars = {
    "--join-primary": palette.colors[0],
    "--join-dark": palette.colors[3],
    "--join-light": palette.colors[1],
    "--join-font-heading": `'${fontPair.heading}', serif`,
    "--join-font-body": `'${fontPair.body}', sans-serif`,
  } as React.CSSProperties;

  const coverImages = wedding.join_cover_images ?? [];
  const frameCount = wedding.join_frame_count ?? 0;

  return (
    <main style={themeVars}>
      <HeroSection
        title={wedding.title}
        dateTime={focusDateTime}
        location={focusLocation}
        slug={slug}
        frameCount={frameCount}
        heroImageUrl={heroUrl}
      />

      <CountdownSection wedding={wedding} />

      <StorySection description={wedding.description} />

      {/* Tek etkinlikte bugünkü tarih/mekan bölümü; iki etkinlikte program. */}
      {program.length < 2 ? (
        <VenueSection dateTime={focusDateTime} location={focusLocation} />
      ) : (
        <section className="bg-[#1b1817] px-6 py-24 text-white">
          <div className="mx-auto max-w-4xl">
            <WeddingProgram entries={program} />
          </div>
        </section>
      )}

      <GallerySection images={coverImages} />

      <JoinFormSection slug={slug} />
    </main>
  );
}
